import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/pages/ClientCategoriesPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HardHat, CheckCircle } from "@phosphor-icons/react";

const ContractorRegisterPage = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const [category, setCategory] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [years, setYears] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) {
      toast.error("Please pick a service category.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contractors/profile", {
        category,
        business_name: businessName,
        phone,
        bio,
        service_area: serviceArea,
        years_experience: Number(years) || 0,
      });
      await refreshUser();
      toast.success("Profile created!");
      navigate("/contractor/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="contractor-register-page">
      <Toaster position="top-right" richColors />
      <Header />
      <main className="mx-auto max-w-3xl px-6 py-16 md:px-10 md:py-20">
        <div className="animate-fade-up">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#C65D47]">
            Contractor onboarding
          </p>
          <div className="mt-3 flex items-start gap-4">
            <div className="hidden h-14 w-14 flex-shrink-0 items-center justify-center rounded-md bg-[#C65D47] md:flex">
              <HardHat size={28} weight="duotone" color="#FFFFFF" />
            </div>
            <div>
              <h1 className="font-display text-4xl font-bold tracking-tight text-[#0A0F0D] sm:text-5xl">
                Register your trade.
              </h1>
              <p className="mt-3 text-base leading-relaxed text-[#5C605E]">
                Tell us about your business so clients in your area can find and hire you.
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-12 space-y-6 rounded-md border border-[#E5E2DB] bg-white p-6 md:p-10"
          data-testid="contractor-register-form"
        >
          <div className="space-y-2">
            <Label className="text-[#0A0F0D]">Service category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                className="border-[#E5E2DB] bg-white"
                data-testid="contractor-category-select"
              >
                <SelectValue placeholder="Choose your trade…" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem
                    key={c.id}
                    value={c.id}
                    data-testid={`contractor-category-option-${c.id}`}
                  >
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="biz" className="text-[#0A0F0D]">Business name</Label>
              <Input
                id="biz"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                placeholder="Doe Electric Ltd."
                data-testid="contractor-business-name-input"
                className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#0A0F0D]">Contact phone</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                required
                pattern="0[0-9]{9}"
                minLength={10}
                maxLength={10}
                inputMode="numeric"
                placeholder="0821234567"
                data-testid="contractor-phone-input"
                className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
              />
              <p className="text-xs text-[#5C605E]">10 digits, South African format (starts with 0).</p>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="area" className="text-[#0A0F0D]">Service area</Label>
              <Input
                id="area"
                value={serviceArea}
                onChange={(e) => setServiceArea(e.target.value)}
                placeholder="e.g. George, Western Cape"
                data-testid="contractor-service-area-input"
                className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="years" className="text-[#0A0F0D]">Years of experience</Label>
              <Input
                id="years"
                type="number"
                min={0}
                value={years}
                onChange={(e) => setYears(e.target.value)}
                data-testid="contractor-years-input"
                className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio" className="text-[#0A0F0D]">Short bio (what makes you great)</Label>
            <Textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={4}
              placeholder="Certified, insured, 10+ years installing residential panels…"
              data-testid="contractor-bio-input"
              className="border-[#E5E2DB] bg-white focus-visible:ring-[#C65D47]"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-[#5C605E]">
              <CheckCircle size={14} weight="bold" className="mr-1 inline align-[-2px]" />
              Your listing goes live immediately.
            </p>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="contractor-submit-button"
              className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
            >
              {submitting ? "Saving…" : "Complete registration"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ContractorRegisterPage;
